import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { toast } from "sonner";
import { Check, Crown, Star, Ticket, Lightning } from "@phosphor-icons/react";

export default function Billing() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState("");
  const [polling, setPolling] = useState(false);
  const [cycle, setCycle] = useState("month");

  const loadSub = () => api.get("/subscription/me").then((r) => setSub(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data));
    loadSub();
  }, []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/subscription/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success(data.purpose === "single_job" ? t("credit_added") : data.purpose === "spoed_job" ? t("spoed_credit_added") : t("pay_success"));
          setPolling(false); loadSub();
          setParams({}); return;
        }
        if (data.status === "expired" || attempts >= 6) {
          toast.error(t("pay_failed")); setPolling(false); setParams({}); return;
        }
      } catch (e) { if (attempts >= 6) { setPolling(false); setParams({}); return; } }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params]);

  const checkout = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/subscription/checkout", { plan_id: planId, interval: cycle, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(""); }
  };

  const buySingleJob = async () => {
    setBusy("single_job");
    try {
      const { data } = await api.post("/jobs/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(""); }
  };

  const buySpoedJob = async () => {
    setBusy("spoed_job");
    try {
      const { data } = await api.post("/jobs/spoed/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(""); }
  };

  const cancelSub = async () => {
    if (!window.confirm(t("cancel_confirm"))) return;
    try { await api.post("/subscription/cancel"); toast.success(t("cancelled_ok")); loadSub(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const accent = { basic: "border-zinc-200", pro: "border-[#E11D2A] ring-2 ring-[#E11D2A]", elite: "border-[#0A0A0A]" };

  return (
    <div className="max-w-5xl mx-auto px-5 py-10">
      <div className="text-center">
        <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight">{t("plans_title")}</h1>
        <p className="text-zinc-500 mt-2">{t("plans_sub")}</p>
        {sub?.plan !== "free" && sub?.current_period_end && (
          <div className="mt-3" data-testid="sub-status">
            {sub.cancel_at_period_end ? (
              <p className="text-sm text-amber-600 font-semibold">{t("sub_ends_on")}: {new Date(sub.current_period_end * 1000).toLocaleDateString()}</p>
            ) : (
              <p className="text-sm text-[#E11D2A] font-semibold">{t("renews_on")}: {new Date(sub.current_period_end * 1000).toLocaleDateString()}</p>
            )}
            {!sub.cancel_at_period_end && (
              <button data-testid="cancel-sub" onClick={cancelSub} className="mt-1 text-sm text-red-600 hover:underline font-medium">{t("cancel_sub")}</button>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-center">
        <div className="inline-flex bg-zinc-100 rounded-full p-1" data-testid="billing-cycle-toggle">
          <button data-testid="cycle-month" onClick={() => setCycle("month")} className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-colors ${cycle === "month" ? "bg-white text-[#E11D2A] shadow-sm" : "text-zinc-500"}`}>{t("billing_monthly")}</button>
          <button data-testid="cycle-year" onClick={() => setCycle("year")} className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-colors ${cycle === "year" ? "bg-white text-[#E11D2A] shadow-sm" : "text-zinc-500"}`}>{t("billing_yearly")} <span className="text-green-600 text-xs">· {t("save_yearly")}</span></button>
        </div>
      </div>

      {polling && <div data-testid="pay-processing" className="mt-6 text-center bg-amber-50 text-amber-700 border border-amber-200 rounded-md py-3">{t("pay_processing")}</div>}

      <div className="mt-10 grid md:grid-cols-3 gap-5">
        {plans.map((p) => {
          const isCurrent = sub?.plan === p.id;
          const price = cycle === "year" ? p.price_year : p.price_month;
          return (
            <div key={p.id} data-testid={`plan-${p.id}`} className={`relative bg-white border rounded-md p-6 flex flex-col ${accent[p.id] || "border-zinc-200"}`}>
              {p.id === "pro" && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E11D2A] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1"><Star size={12} weight="fill" />{t("most_popular")}</div>}
              <div className="flex items-center gap-2">
                {p.id !== "basic" && <Crown weight="fill" className={p.id === "elite" ? "text-[#0A0A0A]" : "text-[#E11D2A]"} size={20} />}
                <h3 className="font-head text-xl font-bold">{p.name}</h3>
              </div>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-head font-black text-4xl tabular-nums">€{price}</span>
                <span className="text-zinc-500 mb-1">{cycle === "year" ? t("per_year") : t("per_month")}</span>
              </div>
              <div className="text-xs text-zinc-400 mt-0.5">{t("excl_vat")}</div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm"><Check weight="bold" className="text-green-600 mt-0.5 shrink-0" size={16} />{f}</li>
                ))}
              </ul>
              <div className="mt-6">
                {isCurrent ? (
                  <div data-testid={`current-${p.id}`} className="text-center text-sm font-semibold text-zinc-500 bg-zinc-100 rounded-md py-2.5">{t("current_plan")}</div>
                ) : (
                  <button data-testid={`checkout-${p.id}`} disabled={busy === p.id} onClick={() => checkout(p.id)}
                    className="w-full font-semibold rounded-md py-2.5 transition-colors disabled:opacity-60 bg-[#E11D2A] text-white hover:bg-[#B0121F]">
                    {busy === p.id ? t("loading") : t("choose_plan")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div data-testid="single-job-card" className="mt-6 bg-white border border-zinc-200 rounded-md p-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-md bg-[#FFCC00]/20 flex items-center justify-center"><Ticket weight="duotone" className="text-[#8a6d00]" size={24} /></div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-head text-lg font-bold">{t("single_job_title")}</h3>
              <span className="font-head font-black text-lg text-[#E11D2A] tabular-nums">€{(sub?.single_job_fee ?? 9.99).toFixed(2).replace(".", ",")}</span>
              <span className="text-xs text-zinc-400">{t("excl_vat")}</span>
            </div>
            <p className="text-sm text-zinc-500">{t("single_job_desc")}</p>
            {sub?.job_credits > 0 && <p className="text-sm text-green-700 font-semibold mt-1" data-testid="job-credits">{t("job_credits")}: {sub.job_credits}</p>}
          </div>
        </div>
        <button data-testid="buy-single-job" disabled={busy === "single_job"} onClick={buySingleJob}
          className="bg-[#FFCC00] text-[#0A0A0A] hover:bg-[#E6B800] font-bold rounded-md px-6 py-2.5 transition-colors disabled:opacity-60">
          {busy === "single_job" ? t("loading") : t("buy_single_job")}
        </button>
      </div>

      <div data-testid="spoed-job-card" className="mt-6 bg-white border border-[#E11D2A]/30 rounded-md p-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-md bg-[#E11D2A]/10 flex items-center justify-center"><Lightning weight="fill" className="text-[#E11D2A]" size={24} /></div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-head text-lg font-bold">{t("spoed_job_title")}</h3>
              <span className="font-head font-black text-lg text-[#E11D2A] tabular-nums">€{(sub?.spoed_job_fee ?? 14.99).toFixed(2).replace(".", ",")}</span>
              <span className="text-xs text-zinc-400">{t("excl_vat")}</span>
            </div>
            <p className="text-sm text-zinc-500">{t("spoed_job_desc")}</p>
            {sub?.spoed_credits > 0 && <p className="text-sm text-green-700 font-semibold mt-1" data-testid="spoed-credits">{t("spoed_credits")}: {sub.spoed_credits}</p>}
          </div>
        </div>
        <button data-testid="buy-spoed-job" disabled={busy === "spoed_job"} onClick={buySpoedJob}
          className="bg-[#E11D2A] text-white hover:bg-[#B0121F] font-bold rounded-md px-6 py-2.5 transition-colors disabled:opacity-60">
          {busy === "spoed_job" ? t("loading") : t("buy_spoed_job")}
        </button>
      </div>

      <div data-testid="spoed-always-note" className="mt-3 flex items-start gap-2 text-sm text-zinc-600 bg-[#E11D2A]/5 border border-[#E11D2A]/20 rounded-md px-4 py-3">
        <Lightning weight="fill" size={18} className="text-[#E11D2A] mt-0.5 shrink-0" />
        <span>Een spoedklus kost <b>altijd €14,99 excl. 21% btw</b> — ook met een abonnement (Basic, Pro én Elite).</span>
      </div>
    </div>
  );
}
